import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Form, { FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import { UpdateTransactionAccountPayload } from '@atlassian/cssxp-types-billing';
import { noop } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import {
  NotificationTypes,
  successNotificationBuilder,
  useNotification,
  useSidebar,
  useUpdateTransactionAccount,
} from '../../..';
import { AddContactFields } from '../../../add-contact-fields';
import './add-customer-contact.scss';

interface AddCustomerContactProps {
  entitlementId: string;
  txaId: string;
  refetch?: () => void;
}

export const AddCustomerContact = ({ entitlementId, txaId, refetch }: AddCustomerContactProps) => {
  const { closeSidebar } = useSidebar();
  const { notificationSent, sendSuccessNotification, setNotificationSent } = useNotification();
  const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(true);
  const [atlassianAccountIds, setAtlassianAccountIds] = useState<Array<string>>(['']);
  const [atlassianAccountEmails, setAtlassianAccountEmails] = useState<Array<string>>(['']);
  const { isProcessing, updateTransactionAccount } = useUpdateTransactionAccount({
    onClose: noop,
    onComplete: () => {
      closeSidebar();
      refetch?.();
    },
    sendNotification: false,
  });

  const handleFormSubmit = useCallback(async () => {
    const updateTransactionAccountPayload: UpdateTransactionAccountPayload = {
      entitlementId,
      modifiedCustomerContacts: {
        emailsOrAaidsToAdd: atlassianAccountIds as string[],
      },
    };
    await updateTransactionAccount({
      transactionAccountId: txaId,
      updateTransactionAccountPayload,
    });
    const commonMessage = `You've added ${atlassianAccountEmails.length} customer contacts:`;
    const message = (
      <div>
        {commonMessage}:
        <ul className="mt-4">
          {atlassianAccountEmails?.map((email) => (
            <li key={email}>{email}</li>
          ))}
        </ul>
      </div>
    );
    if (!notificationSent && !isProcessing) {
      const notificationOptions = successNotificationBuilder()
        .isVersion(NotificationTypes.FLAG)
        .withHeader('Successfully added customer contacts')
        .withMessage(message)
        .withDismissAfter(5000)
        .build();
      sendSuccessNotification(notificationOptions);
      setNotificationSent(true);
    }
  }, [
    atlassianAccountEmails,
    atlassianAccountIds,
    entitlementId,
    isProcessing,
    notificationSent,
    sendSuccessNotification,
    setNotificationSent,
    txaId,
    updateTransactionAccount,
  ]);

  useEffect(() => {
    if (atlassianAccountIds.findIndex((id) => !id) >= 0) {
      setIsSaveDisabled(true);
    } else {
      setIsSaveDisabled(false);
    }
  }, [atlassianAccountIds]);

  return (
    <div className="add-ecc-subscription-details-page">
      <Form onSubmit={handleFormSubmit}>
        {({ formProps }) => (
          <form {...formProps}>
            <FormHeader title={'Add customer contact'} />
            <FormSection>
              <h4 className="customer-contact-text">Customer contact</h4>
              <AddContactFields
                setAtlassianAccountIds={setAtlassianAccountIds}
                atlassianAccountIds={atlassianAccountIds}
                setAtlassianAccountEmails={setAtlassianAccountEmails}
                atlassianAccountEmails={atlassianAccountEmails}
                emailFieldText="Enter contact email"
                nameFieldText="Enter contact full name"
                addButtonText="Add customer contact"
              />
            </FormSection>
            <div className="form-footer">
              <FormFooter align="end">
                <ButtonGroup>
                  <Button appearance="subtle" onClick={closeSidebar} testId="cancel-button">
                    Cancel
                  </Button>
                  <LoadingButton
                    appearance="primary"
                    isLoading={isProcessing}
                    isDisabled={isSaveDisabled}
                    type="submit"
                    testId="save-customer-contact-button"
                  >
                    Save
                  </LoadingButton>
                </ButtonGroup>
              </FormFooter>
            </div>
          </form>
        )}
      </Form>
    </div>
  );
};
